// tog'a #3148 (2026-10-03; #3270: A — bitta «qator», B — joyi yo'q tovarga eslatma): «Места хранения».
// - StorageRowsButton: «Склады» qatorida — shu sklad qatorlari (A-1…) va yo'naltirish qoidalari (artikul / nom / partiya).
// - StorageCell: «Остатки» ustuni — «A-1 (5), B-2*» (* — qoida bo'yicha), bosilsa StoragePlaceModal (partiyalar bo'yicha joy, tarix).
//   Ko'rinadigan barcha qatorlar uchun BITTA so'rov (60 ms ichidagi so'rovlar to'planadi).
// - StorageUnassignedBanner: «Остатки» tepasida — joyi belgilanmagan tovarlar soni va ro'yxati.
// Backend: api/Storage (Helpers/StoragePlaces).
import React, { useEffect, useState } from "react"
import { Alert, Button, Drawer, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Spin, Tabs, Tag, Tooltip, message } from "antd"
import StickyTable from "../table/StickyTable"   // tog'a #4695/#4703: hamma jadvalda sarlavha va «Итого» qotadi
import { useQuery, useQueryClient } from "@tanstack/react-query"
import moment from "moment"
import i18n from "../../services/i18n"
import { request } from "../../utils/config"
import useQueryParams from "../../hooks/useQueryParams"
import { permSync } from "../../utils/permSync"
import { useIsMainSystem } from "../../utils/mainSystem"   // tog'a #4104
import "./storage.less"   // tog'a #3520: zona kartalari

const t = (k: string, d: string) => i18n.t(k, d)
const err = (e: any) => e?.response?.data?.message || e?.response?.data?.Message || e?.message || String(e)

// ---------- qatorlar (sklad) ----------
export function StorageRowsButton({ warehouseId, warehouseName }: { warehouseId: number; warehouseName?: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Tooltip title={t("splTitle", "Места хранения")}>
        <Button size="small" onClick={(e) => { e.stopPropagation(); setOpen(true) }} data-testid="storage-rows-open">📍 {t("stButton", "Места")}</Button>
      </Tooltip>
      {open && <StorageRowsDrawer warehouseId={warehouseId} warehouseName={warehouseName} onClose={() => setOpen(false)} />}
    </>
  )
}

function StorageRowsDrawer({ warehouseId, warehouseName, onClose }: { warehouseId: number; warehouseName?: string; onClose: () => void }) {
  const qc = useQueryClient()
  const canEdit = permSync("warehouses").canEdit
  const key = ["STORAGE_ROWS", warehouseId]
  const zKey = ["STORAGE_ZONES", warehouseId]
  // tog'a #3520: Sklad → ZONA → qator. Rows so'rovi zonasiz eski qatorlarni o'zi «Основная зона» ga o'tkazadi — zonalar undan KEYIN so'raladi
  const { data, isFetching } = useQuery(key, async () => (await request({ url: "Storage/Rows", params: { warehouseId } })).data)
  const zonesQ = useQuery(zKey, async () => (await request({ url: "Storage/Zones", params: { warehouseId } })).data, { enabled: data !== undefined })
  const zones: any[] = zonesQ.data || []
  const qatorlar: any[] = data || []
  const [edit, setEdit] = useState<any>(null)
  const [zEdit, setZEdit] = useState<any>(null)
  const [form] = Form.useForm()
  const [zForm] = Form.useForm()
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (edit) form.setFieldsValue({ sortOrder: 0, ...edit }) }, [edit])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (zEdit) zForm.setFieldsValue({ sortOrder: 0, ...zEdit }) }, [zEdit])   // eslint-disable-line react-hooks/exhaustive-deps
  const yangila = () => { qc.invalidateQueries(key); qc.invalidateQueries(zKey); qc.invalidateQueries(["STORAGE_WHERE"]); storageCacheReset() }
  const saqla = async () => {
    const v = await form.validateFields()
    setBusy(true)
    try {
      if (edit?.id) await request({ url: `Storage/Rows/${edit.id}`, method: "PUT", data: { ...v, warehouseId } })
      else await request({ url: "Storage/Rows", method: "POST", data: { ...v, warehouseId } })
      message.success(t("saved", "Сохранено")); setEdit(null); yangila()
    } catch (e: any) { message.error(err(e)) } finally { setBusy(false) }
  }
  const ochir = async (r: any) => {
    try { await request({ url: `Storage/Rows/${r.id}`, method: "DELETE" }); message.success(t("deleted", "Удалено")); yangila() }
    catch (e: any) { message.error(err(e)) }
  }
  const saqlaZ = async () => {
    const v = await zForm.validateFields()
    setBusy(true)
    try {
      if (zEdit?.id) await request({ url: `Storage/Zones/${zEdit.id}`, method: "PUT", data: { ...v, warehouseId } })
      else await request({ url: "Storage/Zones", method: "POST", data: { ...v, warehouseId } })
      message.success(t("saved", "Сохранено")); setZEdit(null); yangila()
    } catch (e: any) { message.error(err(e)) } finally { setBusy(false) }
  }
  const ochirZ = async (z: any) => {
    try { await request({ url: `Storage/Zones/${z.id}`, method: "DELETE" }); message.success(t("deleted", "Удалено")); yangila() }
    catch (e: any) { message.error(err(e)) }
  }
  const qoida = (r: any) => [
    r.ruleArticle ? <Tag key="a" color="blue">{t("stRuleArticleShort", "артикул")}: {r.ruleArticle}</Tag> : null,
    r.ruleName ? <Tag key="n" color="green">{t("stRuleNameShort", "название")}: {r.ruleName}</Tag> : null,
    r.ruleBatch ? <Tag key="b" color="purple">{t("stRuleBatchShort", "партия")}: {r.ruleBatch}</Tag> : null,
  ].filter(Boolean)
  const columns: any[] = [
    { title: t("stName", "Место"), dataIndex: "name", render: (v: string, r: any) => <div><b>{v}</b>{r.note ? <div style={{ fontSize: 12, opacity: 0.75 }}>{r.note}</div> : null}</div> },
    { title: t("stRules", "Правила"), key: "rules", render: (_: any, r: any) => { const q = qoida(r); return q.length ? q : <span style={{ opacity: 0.6 }}>{t("stNoRules", "только вручную")}</span> } },
    { title: t("stPlaced", "Вручную"), dataIndex: "placed", align: "right" as const },
    ...(canEdit ? [{
      title: "", key: "act", width: 110, render: (_: any, r: any) => (
        <span style={{ display: "inline-flex", gap: 6 }}>
          <Button size="small" onClick={() => setEdit(r)}>✏️</Button>
          <Popconfirm title={t("stDelQ", "Удалить место? Товары в нём станут «не указано».")} onConfirm={() => ochir(r)} okText={t("yes", "Да")} cancelText={t("no", "Нет")}>
            <Button size="small" danger>✕</Button>
          </Popconfirm>
        </span>
      ),
    }] : []),
  ]
  return (
    <Drawer visible destroyOnClose zIndex={1000} width="min(860px, 100vw)" onClose={onClose} title={`📍 ${t("splTitle", "Места хранения")}${warehouseName ? " — " + warehouseName : ""}`} data-testid="storage-rows-drawer">
      <div onClick={(e) => e.stopPropagation()}>
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={t("znHelp", "Склад делится на зоны (помещения, ангары), в каждой зоне — свои места (ряды A-1, A-2…). Если зон две и больше, место везде показывается как «Зона · A-1».")}
          description={t("stHelp", "Места (ряды) склада, например A-1, A-2. Правило направляет товар в ряд автоматически: артикул начинается с…, в названии есть…, партия равна… (несколько значений — через запятую). Вручную можно переложить в «Остатки» → «📍 Место».")} />
        {canEdit && <Button type="primary" onClick={() => setZEdit({})} style={{ marginBottom: 12 }} data-testid="storage-zone-add">+ {t("znAdd", "Добавить зону")}</Button>}
        <Spin spinning={isFetching || zonesQ.isFetching}>
          {zones.length === 0 && !isFetching && !zonesQ.isLoading && !zonesQ.isFetching && (
            <Empty description={t("stNone", "Мест пока нет")}>
              {canEdit && <Button onClick={() => setEdit({})} data-testid="storage-row-add">+ {t("stAdd", "Добавить место")}</Button>}
            </Empty>
          )}
          {zones.map((z) => (
            <div key={z.id} className="storage-zone" data-testid="storage-zone">
              <div className="storage-zone-head">
                <b className="storage-zone-name">🏷 {z.name}</b>
                {z.note ? <span className="storage-zone-note">{z.note}</span> : null}
                <Tag>{t("znRows", "мест")}: {qatorlar.filter((r) => r.zoneId === z.id).length}</Tag>
                <span style={{ flex: 1 }} />
                {canEdit && (
                  <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
                    <Button size="small" type="primary" ghost onClick={() => setEdit({ zoneId: z.id })} data-testid="storage-row-add">+ {t("stAdd", "Добавить место")}</Button>
                    <Button size="small" onClick={() => setZEdit(z)} data-testid="storage-zone-edit" title={t("znEdit", "Изменить зону")}>✏️</Button>
                    <Popconfirm title={t("znDelQ", "Удалить зону? Удалить можно только пустую зону.")} onConfirm={() => ochirZ(z)} okText={t("yes", "Да")} cancelText={t("no", "Нет")}>
                      <Button size="small" danger data-testid="storage-zone-del">✕</Button>
                    </Popconfirm>
                  </span>
                )}
              </div>
              <StickyTable size="small" rowKey="id" pagination={false} scroll={{ x: 520 }} dataSource={qatorlar.filter((r) => r.zoneId === z.id)}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("znEmpty", "В зоне пока нет мест")} /> }} columns={columns} />
            </div>
          ))}
        </Spin>
      </div>
      <Modal visible={!!edit} title={edit?.id ? t("stEdit", "Изменить место") : t("stAdd", "Добавить место")} onCancel={() => setEdit(null)} onOk={saqla} confirmLoading={busy} okText={t("save", "Сохранить")} cancelText={t("cancel", "Отмена")} zIndex={1010} destroyOnClose>
        <Form form={form} layout="vertical" preserve={false}>
          {zones.length > 0 && (
            <Form.Item name="zoneId" label={t("znZone", "Зона")} rules={[{ required: true, message: t("znZone", "Зона") }]}>
              <Select options={zones.map((z) => ({ value: z.id, label: z.name }))} data-testid="storage-row-zone" />
            </Form.Item>
          )}
          <Form.Item name="name" label={t("stName", "Место")} rules={[{ required: true, message: t("stNameReq", "Например A-1") }]}><Input maxLength={60} placeholder="A-1" data-testid="storage-row-name" /></Form.Item>
          <Form.Item name="note" label={t("splNote", "Описание")}><Input maxLength={300} placeholder={t("stNotePh", "например: левый ряд, 2-й стеллаж")} /></Form.Item>
          <Form.Item name="ruleArticle" label={t("stRuleArticle", "Артикул начинается с")} help={t("stRuleHelp", "несколько значений — через запятую")}><Input maxLength={500} placeholder="W-04, VEL-" data-testid="storage-row-rule-article" /></Form.Item>
          <Form.Item name="ruleName" label={t("stRuleName", "В названии есть")}><Input maxLength={500} placeholder="ECO, Premium" /></Form.Item>
          <Form.Item name="ruleBatch" label={t("stRuleBatch", "Партия равна")}><Input maxLength={500} placeholder="801, 802" /></Form.Item>
          <Form.Item name="sortOrder" label={t("stOrder", "Порядок (если подходят несколько мест — берётся меньший)")}><InputNumber min={0} max={9999} /></Form.Item>
        </Form>
      </Modal>
      <Modal visible={!!zEdit} title={zEdit?.id ? t("znEdit", "Изменить зону") : t("znAdd", "Добавить зону")} onCancel={() => setZEdit(null)} onOk={saqlaZ} confirmLoading={busy} okText={t("save", "Сохранить")} cancelText={t("cancel", "Отмена")} zIndex={1010} destroyOnClose>
        <Form form={zForm} layout="vertical" preserve={false}>
          <Form.Item name="name" label={t("znName", "Название зоны")} rules={[{ required: true, message: t("znName", "Название зоны") }]}><Input maxLength={60} placeholder={t("znNamePh", "например: Ангар, Зона 2")} data-testid="storage-zone-name" /></Form.Item>
          <Form.Item name="note" label={t("splNote", "Описание")}><Input maxLength={300} /></Form.Item>
          <Form.Item name="sortOrder" label={t("znOrder", "Порядок (меньше — выше в списке и первой при выборе места по правилу)")}><InputNumber min={0} max={9999} /></Form.Item>
        </Form>
      </Modal>
    </Drawer>
  )
}

// ---------- «Остатки» ustuni: to'plab so'rash ----------
type Place = { warehouseId: number; warehouse?: string; text?: string; spots?: any[] }
const kesh = new Map<string, Place[]>()
let kutish = new Map<string, Array<(v: Place[]) => void>>()
let taymer: any = null
async function yubor() {
  taymer = null
  const navbat = kutish; kutish = new Map()
  const byWh = new Map<number, number[]>()
  for (const k of Array.from(navbat.keys())) { const [w, p] = k.split(":").map(Number); if (!byWh.has(w)) byWh.set(w, []); byWh.get(w)!.push(p) }
  for (const [wh, pids] of Array.from(byWh.entries())) {
    for (let i = 0; i < pids.length; i += 200) {
      const bo = pids.slice(i, i + 200)
      let res: any[] = []
      try { res = (await request({ url: "Storage/Where", params: { productIds: bo.join(","), warehouseId: wh } })).data || [] } catch { res = [] }
      for (const p of bo) {
        const v: Place[] = (res.find((x: any) => x.productId === p)?.places || []) as Place[]
        const k = `${wh}:${p}`; kesh.set(k, v); (navbat.get(k) || []).forEach((cb) => cb(v))
      }
    }
  }
}
function joylar(wh: number, pid: number): Promise<Place[]> {
  const k = `${wh}:${pid}`
  if (kesh.has(k)) return Promise.resolve(kesh.get(k)!)
  return new Promise((res) => { if (!kutish.has(k)) kutish.set(k, []); kutish.get(k)!.push(res); if (!taymer) taymer = setTimeout(yubor, 60) })
}
export const storageCacheReset = () => kesh.clear()

// tog'a #213 (2026-10-19): telefonda (hover yo'q) Tooltip bosilganda ochilib QOLARDI va kartadagi tovar nomini yopardi —
// sensorli ekranda maslahat ko'rsatilmaydi (havola baribir oynani ochadi)
const sensorli = typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(hover: none)").matches

export function StorageCell({ productId, productName }: { productId: number; productName?: string }) {
  const query = useQueryParams()
  const wh = Number(query.get("warehouseId")) || 0
  const [v, setV] = useState<Place[] | null>(null)
  const [open, setOpen] = useState(false)
  const [rev, setRev] = useState(0)
  useEffect(() => { let alive = true; setV(null); joylar(wh, productId).then((x) => alive && setV(x)); return () => { alive = false } }, [wh, productId, rev])
  if (!productId) return null
  const matn = (v || []).map((p) => (wh > 0 ? p.text : `${p.warehouse}: ${p.text}`)).filter(Boolean).join(" · ")
  const havola = (
    <a onClick={(e) => { e.stopPropagation(); setOpen(true) }} data-testid="storage-cell" style={{ whiteSpace: "normal" }}>
      {v == null ? "…" : matn ? <>📍 {matn}</> : <span style={{ color: "#d97706" }}>📍 {t("stUnset", "не указано")}</span>}
    </a>
  )
  return (
    <>
      {sensorli ? havola : (
        <Tooltip title={matn ? t("stCellHint", "* — по правилу места; нажмите, чтобы переложить") : t("stCellNone", "Место не указано — нажмите, чтобы указать")}>
          {havola}
        </Tooltip>
      )}
      {open && <StoragePlaceModal productId={productId} productName={productName} warehouseId={wh} onClose={(changed) => { setOpen(false); if (changed) { kesh.clear(); setRev((x) => x + 1) } }} />}
    </>
  )
}

// ---------- joylashtirish oynasi ----------
export function StoragePlaceModal({ productId, productName, warehouseId, onClose }: { productId: number; productName?: string; warehouseId: number; onClose: (changed: boolean) => void }) {
  const bm = useIsMainSystem()   // tog'a #4104: hamkor tizimida partiya ustuni yo'q (qator bo'yicha joy tanlash o'zgarmaydi)
  const [wh, setWh] = useState<number>(warehouseId || 0)
  // tog'a #3520 (1-band): oynada faqat shu tovarning QOLDIG'I bor skladlar (boshqa skladlar tortilmaydi); bitta bo'lsa — tanlovsiz o'sha sklad
  const stockWh = useQuery(["STORAGE_STOCK_WH", productId], async () => (await request({ url: "Storage/StockWarehouses", params: { productId } })).data || [], { enabled: !warehouseId, retry: false })
  const omborlar: any[] = stockWh.data || []
  useEffect(() => { if (!warehouseId && !wh && omborlar.length === 1) setWh(omborlar[0].warehouseId) }, [stockWh.data])   // eslint-disable-line react-hooks/exhaustive-deps
  const det = useQuery(["STORAGE_DETAIL", productId, wh], async () => (await request({ url: "Storage/Detail", params: { productId, warehouseId: wh } })).data, { enabled: wh > 0, retry: false })
  const tarix = useQuery(["STORAGE_HISTORY", productId, wh], async () => (await request({ url: "Storage/History", params: { productId, warehouseId: wh } })).data, { enabled: wh > 0 })
  const [changed, setChanged] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const rows: any[] = det.data?.rows || []
  const zones: any[] = det.data?.zones || []   // #3520: avval zona, keyin SHU zonaning qatorlari
  const joyla = async (batch: string | null, rowId: number | null, qty?: number | null) => {
    setBusy(batch ?? "__all")
    try {
      await request({ url: "Storage/Place", method: "POST", data: { warehouseId: wh, productId, batch: batch || undefined, rowId: rowId || 0, qty: qty || undefined } })
      message.success(t("stPlacedOk", "Место сохранено")); setChanged(true); det.refetch(); tarix.refetch()
    } catch (e: any) { message.error(err(e)) } finally { setBusy(null) }
  }
  const joyMatn = (pl: any[]) => (pl || []).map((p: any) => `${p.row}${p.qty != null ? ` (${p.qty})` : ""}${p.source === "rule" ? "*" : ""}`).join(", ")
  const Tanla = ({ batch, cur, max }: { batch: string | null; cur: any[]; max?: number }) => {
    const curRow = cur?.find((p: any) => p.source === "manual")?.rowId
    const [zone, setZone] = useState<number | undefined>(rows.find((r) => r.id === curRow)?.zoneId ?? zones[0]?.id)
    const [row, setRow] = useState<number | undefined>(curRow)
    const [qty, setQty] = useState<number | null>(null)
    const zRows = zones.length > 1 ? rows.filter((r) => r.zoneId === zone) : rows
    return (
      <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {zones.length > 1 && (
          <Select size="small" style={{ minWidth: 130 }} value={zone} onChange={(v) => { setZone(v); setRow(undefined) }} placeholder={t("znZone", "Зона")}
            options={zones.map((z) => ({ value: z.id, label: z.name }))} data-testid="storage-place-zone" />
        )}
        <Select size="small" style={{ minWidth: 120 }} value={row} onChange={setRow} placeholder={t("stPick", "Место")} options={[{ value: 0, label: t("stUnsetOpt", "— не указано —") }, ...zRows.map((r) => ({ value: r.id, label: r.name }))]} data-testid="storage-place-row" />
        <InputNumber size="small" min={0.01} max={max} value={qty as any} onChange={(v) => setQty(v == null ? null : Number(v))} placeholder={t("stQtyAll", "всё")} style={{ width: 90 }} />
        <Button size="small" type="primary" loading={busy === (batch ?? "__all")} onClick={() => joyla(batch, row ?? 0, qty)} data-testid="storage-place-save">{t("save", "Сохранить")}</Button>
      </span>
    )
  }
  return (
    <Modal visible title={`📍 ${t("stPlaceTitle", "Место хранения")} — ${productName || det.data?.product?.name || "#" + productId}`} onCancel={() => onClose(changed)} footer={null} width={760} zIndex={1010} destroyOnClose>
      <div onClick={(e) => e.stopPropagation()} data-testid="storage-place-modal">
        {!warehouseId && omborlar.length > 0 && (
          <div style={{ marginBottom: 10 }} data-testid="storage-place-wh">
            {omborlar.length === 1 ? (
              <span>{t("warehouse", "Склад")}: <b>{omborlar[0].name}</b> <Tag style={{ marginLeft: 4 }}>{omborlar[0].qty}</Tag></span>
            ) : (
              <span>{t("znStockWh", "Склад (есть остаток)")}: <Select style={{ minWidth: 220 }} value={wh || undefined} onChange={(v) => setWh(Number(v))} placeholder={t("selectWarehouse", "Выберите склад")}
                options={omborlar.map((w: any) => ({ value: w.warehouseId, label: `${w.name} (${w.qty})` }))} data-testid="storage-place-wh-select" /></span>
            )}
          </div>
        )}
        {!warehouseId && stockWh.isLoading ? <Spin /> : !warehouseId && omborlar.length === 0 && wh <= 0 ? <Empty description={t("znNoStock", "Этого товара нет в остатке ни на одном складе")} /> : wh <= 0 ? <Empty description={t("stPickWh", "Выберите склад")} /> : det.isLoading ? <Spin /> : det.isError ? <Alert type="error" message={err(det.error)} /> : (
          <Tabs>
            <Tabs.TabPane key="p" tab={t("stTabPlace", "Место")}>
                <>
                  {rows.length === 0 && <Alert type="warning" showIcon style={{ marginBottom: 10 }} message={t("stNoRowsWh", "На этом складе ещё нет мест — добавьте их в «Склады» → «📍 Места».")} />}
                  <div style={{ marginBottom: 10 }}>
                    <b>{t("stWhole", "Весь товар на складе")}:</b> {joyMatn(det.data?.whole) || <span style={{ color: "#d97706" }}>{t("stUnset", "не указано")}</span>}
                    <div style={{ marginTop: 6 }}><Tanla batch={null} cur={det.data?.whole || []} /></div>
                  </div>
                  <StickyTable size="small" rowKey="batch" pagination={false} scroll={{ x: 680 }} dataSource={det.data?.batches || []} locale={{ emptyText: t("stNoBatches", "Нет остатка на этом складе") }}
                    columns={[
                      ...(bm ? [{ title: t("batchLbl", "Партия"), dataIndex: "batch", render: (v: string) => <b>{v || "—"}</b> }] : []),   // tog'a #4104: faqat glavniy tizimda
                      { title: t("count", "Кол-во"), dataIndex: "qty", align: "right" as const },
                      { title: t("stName", "Место"), key: "pl", width: 190, render: (_: any, b: any) => <span style={{ whiteSpace: "normal" }}>{joyMatn(b.places) || <span style={{ color: "#d97706" }}>{t("stUnset", "не указано")}</span>}</span> },   // #3520: «Зона · A-1» kesilmasin
                      { title: "", key: "act", render: (_: any, b: any) => <Tanla batch={b.batch || null} cur={b.places || []} max={b.qty} /> },
                    ]} />
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{t("stLegend", "* — место по правилу. Количество пусто — вся партия в этом месте; с количеством — часть (одна партия может лежать в нескольких местах).")}</div>
                </>
            </Tabs.TabPane>
            <Tabs.TabPane key="h" tab={t("stTabHistory", "История")}>
                <StickyTable size="small" rowKey="id" pagination={false} loading={tarix.isFetching} dataSource={tarix.data || []}
                  columns={[
                    { title: t("date", "Дата"), dataIndex: "at", render: (v: string) => moment(v).format("DD.MM.YYYY HH:mm") },
                    ...(bm ? [{ title: t("batchLbl", "Партия"), dataIndex: "batch", render: (v: string) => v || "—" }] : []),   // tog'a #4104: faqat glavniy tizimda
                    { title: t("stFrom", "Откуда"), dataIndex: "from", render: (v: string) => v || "—" },
                    { title: t("stTo", "Куда"), dataIndex: "to", render: (v: string) => v || t("stUnset", "не указано") },
                    { title: t("count", "Кол-во"), dataIndex: "qty", render: (v: number) => (v == null ? t("stQtyAll", "всё") : v) },
                    { title: t("who", "Кто"), dataIndex: "userName" },
                  ]} />
            </Tabs.TabPane>
          </Tabs>
        )}
      </div>
    </Modal>
  )
}

// ---------- «не указано» eslatmasi ----------
export function StorageUnassignedBanner() {
  const query = useQueryParams()
  const wh = Number(query.get("warehouseId")) || 0
  const { data } = useQuery(["STORAGE_UNASSIGNED", wh], async () => (await request({ url: "Storage/Unassigned", params: { warehouseId: wh } })).data, { staleTime: 60000, retry: false })
  const [open, setOpen] = useState(false)
  const [pick, setPick] = useState<any>(null)
  const n = Number(data?.count || 0)
  if (!n) return null
  return (
    <>
      <Alert type="warning" showIcon data-testid="storage-unassigned" style={{ marginBottom: 10 }}
        message={<span>📍 {t("stUnassignedMsg", "Место не указано у товаров")}: <b>{n}</b> <a onClick={() => setOpen(true)} style={{ marginLeft: 8 }}>{t("stShow", "показать")}</a></span>} />
      <Drawer visible={open} onClose={() => setOpen(false)} width="min(640px, 100vw)" zIndex={1000} title={"📍 " + t("stUnassignedTitle", "Место не указано")}>
        <StickyTable size="small" rowKey={(r: any) => r.warehouseId + "-" + r.productId} pagination={false} dataSource={data?.items || []}
          columns={[
            { title: t("warehouse", "Склад"), dataIndex: "warehouse" },
            { title: t("product", "Товар"), dataIndex: "name", render: (v: string, r: any) => <div><b>{v}</b>{r.article ? <div style={{ fontSize: 11, opacity: 0.7 }}>{r.article}</div> : null}</div> },
            { title: t("count", "Кол-во"), dataIndex: "qty", align: "right" as const },
            { title: "", key: "a", render: (_: any, r: any) => <Button size="small" onClick={() => setPick(r)}>📍 {t("stSetPlace", "Указать")}</Button> },
          ]} />
      </Drawer>
      {pick && <StoragePlaceModal productId={pick.productId} productName={pick.name} warehouseId={pick.warehouseId} onClose={() => { setPick(null); kesh.clear() }} />}
    </>
  )
}

/** Zakaz qabul oynasida (sklad bo'yicha taqsim) — tovar shu skladning qaysi joyida: «📍 A-1, B-2*». Joy bo'lmasa — hech narsa. */
export function StorageHint({ productId, warehouseId }: { productId: number; warehouseId: number }) {
  const [v, setV] = useState<Place[] | null>(null)
  useEffect(() => { let alive = true; if (productId > 0 && warehouseId > 0) joylar(warehouseId, productId).then((x) => alive && setV(x)); return () => { alive = false } }, [productId, warehouseId])
  const matn = (v || []).map((p) => p.text).filter(Boolean).join(", ")
  if (!matn) return null
  return <div style={{ fontSize: 11, color: "#0ea5e9" }} data-testid="storage-hint">📍 {matn}</div>
}
